import React, { useMemo, useState } from "react";
import { Link2, Unlink2, Check, ArrowRight, RotateCcw, Hammer } from "lucide-react";
import { SoundToggle } from "@/components/SoundToggle";
import { playResult, playFanfare } from "@/lib/feedbackSounds";
import { Stave, Single, Beamed, pairDirections, toClef, viewBox, ClefCtx, CLEF_PAD } from "@/components/QuaverDraw";

const ROUND = 10;
const TREBLE = ["E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5"];
const rnd = (a) => a[Math.floor(Math.random() * a.length)];
const X = (slot) => 16 + slot * 24;

// Each beat is a crotchet ("q") or two quavers ("e"); at least two beats of quavers so there's beaming to do.
const makeBar = (clef) => {
  const PITCHES = toClef(TREBLE, clef);
  let beats;
  do beats = Array.from({ length: 4 }, () => (Math.random() < 0.6 ? "e" : "q")); while (beats.filter((b) => b === "e").length < 2);
  const notes = [];
  beats.forEach((b, i) => {
    if (b === "q") notes.push({ slot: i * 2, beat: i, kind: "q", p: rnd(PITCHES) });
    else notes.push({ slot: i * 2, beat: i, kind: "e", p: rnd(PITCHES) }, { slot: i * 2 + 1, beat: i, kind: "e", p: rnd(PITCHES) });
  });
  return notes;
};
// Gaps between two quavers that sit next to each other (a join learners can switch on or off).
const gapsOf = (notes) => notes.slice(0, -1).map((n, i) => (n.kind === "e" && notes[i + 1].kind === "e" && notes[i + 1].slot === n.slot + 1 ? i : null)).filter((i) => i != null);

const BarView = ({ notes, joins, clef }) => {
  const parts = [];
  for (let i = 0; i < notes.length;) {
    let j = i;
    while (notes[i].kind === "e" && joins.has(j)) j += 1;
    if (j > i) {
      const g = notes.slice(i, j + 1);
      parts.push(<Beamed key={i} xs={g.map((n) => X(n.slot))} ps={g.map((n) => n.p)} up={pairDirections(g.map((n) => n.p), clef)[0] === "up"} />);
    } else parts.push(<Single key={i} x={X(notes[i].slot)} p={notes[i].p} quaver={notes[i].kind === "e"} />);
    i = j + 1;
  }
  return (
    <svg viewBox={viewBox(0, 12, 206, 120, clef)} className="w-full text-foreground" role="img" aria-label="A bar of 4/4 to beam">
      <Stave x1={2} x2={203} /><line x1="203" x2="203" y1="60" y2="108" stroke="currentColor" strokeWidth="1.4" />
      {[1, 2, 3].map((b) => <line key={b} x1={X(b * 2) - 12} x2={X(b * 2) - 12} y1="114" y2="122" stroke="currentColor" strokeOpacity="0.35" />)}
      {parts}
    </svg>
  );
};

// Lesson 4: tap the gap between two quavers to join (or split) a beam, then check the bar.
export const BeamBuilder = ({ testId = "beam-builder", clef = "treble" }) => {
  const bass = clef === "bass", pad = bass ? CLEF_PAD : 0, newBar = () => makeBar(clef);
  const [notes, setNotes] = useState(newBar);
  const [joins, setJoins] = useState(new Set());
  const [checked, setChecked] = useState(null);
  const [n, setN] = useState(1);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const gaps = useMemo(() => gapsOf(notes), [notes]);
  const toggle = (g) => { if (checked) return; const s = new Set(joins); s.has(g) ? s.delete(g) : s.add(g); setJoins(s); };
  const check = () => {
    const wrongJoin = gaps.some((g) => joins.has(g) && notes[g].beat !== notes[g + 1].beat);
    const missing = gaps.some((g) => !joins.has(g) && notes[g].beat === notes[g + 1].beat);
    const ok = !wrongJoin && !missing;
    setChecked({ ok, why: ok ? "Each pair of quavers is beamed within its own beat." : wrongJoin ? "A beam crosses from one beat into the next. Only join the two quavers inside the same beat." : "Some quavers in the same beat are still separate. Join each pair that makes one beat." });
    playResult(ok);
    if (ok) setScore((s) => s + 1);
  };
  const next = () => {
    if (n === ROUND) { setDone(true); if (score === ROUND) playFanfare(0.2, bass ? "Beam Builder (bass clef)" : "Beam Builder"); return; }
    setNotes(newBar()); setJoins(new Set()); setChecked(null); setN(n + 1);
  };
  const restart = () => { setNotes(newBar()); setJoins(new Set()); setChecked(null); setN(1); setScore(0); setDone(false); };

  return (
    <ClefCtx.Provider value={clef}>
    <div className="mt-6 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><Hammer size={14} /> Beam Builder{bass ? " · bass clef" : ""}</p>
        <SoundToggle testId={`${testId}-sound-toggle`} className="ml-auto" />
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-progress`}>{done ? `${ROUND} of ${ROUND}` : `Bar ${n} of ${ROUND}`} · Score {score}</p>
      </div>
      {done ? (
        <div className="mt-4 text-center">
          <p className="text-base font-semibold" data-testid={`${testId}-result`}>{score === ROUND ? `Perfect! ${score} out of ${ROUND} — you earned a star.` : `You beamed ${score} out of ${ROUND} bars correctly.`}</p>
          <button type="button" onClick={restart} data-testid={`${testId}-restart`} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground"><RotateCcw size={14} /> Play again</button>
        </div>
      ) : (
        <>
          <p className="mt-3 text-sm text-muted-foreground">Tap the <Link2 size={13} className="inline text-primary" /> buttons between quavers to join them with a beam (tap again to split). Then press Check.</p>
          <div className={`mt-3 rounded-xl border-2 bg-background/60 p-2 ${!checked ? "border-border" : checked.ok ? "border-emerald-500" : "border-red-500"}`} data-testid={`${testId}-bar`}>
            <BarView notes={notes} joins={joins} clef={clef} />
            <div className="relative h-11">
              {gaps.map((g) => {
                const left = (((X(notes[g].slot) + X(notes[g + 1].slot)) / 2 + pad) / (206 + pad)) * 100, on = joins.has(g);
                return (
                  <button key={g} type="button" onClick={() => toggle(g)} disabled={!!checked} aria-pressed={on} data-testid={`${testId}-gap-${g}`}
                    aria-label={on ? "Split these quavers" : "Join these quavers"} style={{ left: `${left}%` }}
                    className={`absolute top-0 flex -translate-x-1/2 items-center justify-center rounded-full border-2 transition-colors ${bass ? "h-6 w-6 min-[380px]:h-7 min-[380px]:w-7 sm:h-10 sm:w-10" : "h-10 w-10"} ${on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-primary hover:border-primary"}`}>
                    {on ? <Unlink2 size={14} /> : <Link2 size={14} />}
                  </button>
                );
              })}
            </div>
          </div>
          {!checked ? (
            <div className="mt-3 flex justify-center">
              <button type="button" onClick={check} data-testid={`${testId}-check`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground"><Check size={15} /> Check</button>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3" data-testid={`${testId}-feedback`}>
              <p className={`text-sm font-semibold ${checked.ok ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>{checked.ok ? "Correct! " : "Not quite. "}{checked.why}</p>
              <button type="button" onClick={next} data-testid={`${testId}-next`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground">{n === ROUND ? "See my score" : "Next bar"} <ArrowRight size={13} /></button>
            </div>
          )}
        </>
      )}
    </div>
    </ClefCtx.Provider>
  );
};
